/**
 * Home "Morning Bulletin": what the page says, built from data only.
 * Shared by pages/Home.tsx and its static twin (scripts/prerender-markup.mjs),
 * and checked by scripts/fact-check.mjs, so every line on it traces to a
 * source: notices to their official page, counts to the API.
 * No imports: the prerender loads this file directly with bun.
 */

export const NOTICE_WINDOW_DAYS = 14;
const ALSO_COUNT = 4;

/** Hosts a public-health notice may link to. Anything else is not shown. */
export const OFFICIAL_NOTICE_HOSTS = ["act.gov.au", "health.gov.au", "tga.gov.au", "nsw.gov.au"] as const;

export interface BulletinNotice {
  title: string;
  summary: string | null;
  level: 1 | 2 | 3 | 4;
  publishedAt: string;
  sourceUrl: string | null;
  isCurrent?: boolean;
}

export interface BulletinInput {
  /** null while the notices have not loaded. */
  notices: BulletinNotice[] | null;
  specialistCount: number | null;
  acceptingCount: number | null;
  now: Date;
}

export interface BulletinCondition {
  key: "public-health" | "accepting" | "listed";
  label: string;
  value: string;
  detail: string;
  to: string;
}

export interface Bulletin {
  dateLabel: string;
  conditions: BulletinCondition[];
  lead: (BulletinNotice & { eyebrow: string; tone: "alert" | "calm"; sourceLabel: string }) | null;
  /** Shown when nothing is current, so the lead card is never mistaken for news. */
  quietNote: string | null;
  also: Array<BulletinNotice & { sourceLabel: string }>;
  alsoHeading: string;
}

const LEVEL_LABEL = { 4: "Urgent", 3: "Warning", 2: "Advisory", 1: "Update" } as const;

export function isOfficialNoticeUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    const { protocol, hostname } = new URL(url);
    return protocol === "https:" && OFFICIAL_NOTICE_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
}

export function noticeSourceLabel(url: string): string {
  const host = new URL(url).hostname;
  if (host.endsWith("act.gov.au")) return "ACT Health";
  if (host.endsWith("tga.gov.au")) return "TGA";
  if (host.endsWith("health.gov.au")) return "Australian Government Department of Health";
  return "NSW Health";
}

function isCurrent(notice: BulletinNotice, now: Date): boolean {
  if (typeof notice.isCurrent === "boolean") return notice.isCurrent;
  return now.getTime() - new Date(notice.publishedAt).getTime() <= NOTICE_WINDOW_DAYS * 86_400_000;
}

/** "18 Oct 2026" in Canberra time. */
export function bulletinShortDate(iso: string | Date): string {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "Australia/Sydney" });
}

export function buildBulletin(input: BulletinInput): Bulletin {
  // Only notices we can send the reader to the official page for.
  const notices = (input.notices ?? [])
    .filter((notice) => isOfficialNoticeUrl(notice.sourceUrl))
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  const current = notices.filter((notice) => isCurrent(notice, input.now));
  const leadNotice = [...current].sort((a, b) => b.level - a.level)[0] ?? notices[0] ?? null;
  const leadIsCurrent = leadNotice !== null && current.includes(leadNotice);

  const lead = leadNotice
    ? {
        ...leadNotice,
        sourceLabel: noticeSourceLabel(leadNotice.sourceUrl as string),
        eyebrow: leadIsCurrent
          ? `${noticeSourceLabel(leadNotice.sourceUrl as string)} · ${LEVEL_LABEL[leadNotice.level]} · ${bulletinShortDate(leadNotice.publishedAt)}`
          : `Most recent notice · ${bulletinShortDate(leadNotice.publishedAt)}`,
        tone: leadIsCurrent && leadNotice.level >= 3 ? ("alert" as const) : ("calm" as const),
      }
    : null;

  const also = notices
    .filter((notice) => notice !== leadNotice)
    .slice(0, ALSO_COUNT)
    .map((notice) => ({ ...notice, sourceLabel: noticeSourceLabel(notice.sourceUrl as string) }));

  const conditions: BulletinCondition[] = [
    {
      key: "public-health",
      label: "Public health",
      value: input.notices === null ? "Checking…" : current.length === 0 ? "No new notices" : `${current.length} current notice${current.length === 1 ? "" : "s"}`,
      detail: `Last ${NOTICE_WINDOW_DAYS} days · official sources`,
      to: "/alerts",
    },
    {
      key: "accepting",
      label: "Accepting referrals",
      value: input.acceptingCount === null ? "Checking…" : input.acceptingCount === 0 ? "None confirmed yet" : String(input.acceptingCount),
      detail: "Only when the practice confirms it",
      to: "/directory?type=specialist&accepting=yes",
    },
    {
      key: "listed",
      label: "Specialists listed",
      value: input.specialistCount === null ? "Checking…" : String(input.specialistCount),
      detail: "Each shows its source and check date",
      to: "/directory",
    },
  ];

  return {
    dateLabel: input.now.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Australia/Sydney" }),
    conditions,
    lead,
    quietNote: lead && !leadIsCurrent ? `No new public health notices in the last ${NOTICE_WINDOW_DAYS} days.` : null,
    also,
    alsoHeading: lead && also.every((notice) => notice.sourceLabel === lead.sourceLabel) ? `Also from ${lead.sourceLabel}` : "Earlier notices",
  };
}
